import React, { SyntheticEvent, useEffect, useState } from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import { Equipment } from '../../redux/api/equipment/equipment.model';
import StringUtils from '../../utils/stringUtils';
import CreateDriver from '../../pages/Drivers/CreateDriver/CreateDriver';
import { CarrierResponse } from '../../redux/api/carriers/carriers.model';
import { Driver } from '../../redux/api/driver/driver.model';
import { SxProps } from '@mui/material';
import useAuth from '../../providers/Auth/Auth.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import useDriverSearch from '../../hooks/useDriverSearch';

type DriverSelectorProps = {
  label?: string;
  selectedDriver: TwAutocompleteOption | null | undefined;
  hasError: boolean;
  onChange: (value: string) => void;
  setSelectedDriver: (driver: TwAutocompleteOption | null) => void;
  setOpenDriverModal?: (open: boolean) => void;
  fromDocManager?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps;
  noActions?: boolean;
  hideLabel?: boolean;
  /**
   * If provided, will fetch and return the drivers with the given ids
   * currently used for Owner Operator settlements
   */
  driversIdsToFilter?: string[];
};

const TwDriverSelector: React.FC<DriverSelectorProps> = ({
  label,
  selectedDriver,
  hasError,
  setSelectedDriver,
  setOpenDriverModal,
  onChange,
  fromDocManager = false,
  disabled = false,
  readOnly = false,
  sx = {},
  noActions = false,
  hideLabel = false,
  driversIdsToFilter
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const [openCreateDriverModal, setOpenCreateDriverModal] = useState<boolean>(false);
  const [driverFilterLabels, setDriverFilterLabels, fetchDriversForFilter] =
    useDriverSearch(driversIdsToFilter);
  const [disableNoOption, setDisableNoOption] = useState<boolean>(true);

  const getDriverName = (driver: Driver): string => {
    return `${StringUtils.capitalizeFirstLetter(
      driver.first_name.toLowerCase()
    )} ${StringUtils.capitalizeFirstLetter(driver.last_name.toLowerCase())}`;
  };

  // Open create driver modal
  const handleOpenCreateDriver = (): void => {
    setOpenDriverModal && setOpenDriverModal(true);
    setOpenCreateDriverModal(true);
  };

  // Close create driver modal
  const handleCloseCreateDriver = (): void => {
    setOpenDriverModal && setOpenDriverModal(false);
    setOpenCreateDriverModal(false);
  };

  // Create driver success
  const handleCreateDriverSuccess = (
    newDriver: Driver,
    selectedTruck?: Equipment | undefined,
    selectedCarrier?: CarrierResponse | undefined,
    selectedTrailer?: Equipment | undefined
  ): void => {
    setSelectedDriver({
      id: newDriver.id,
      value: newDriver.id,
      label: getDriverName(newDriver),
      subLabel: newDriver.email_address
    });
    onChange(newDriver.id);
  };

  return (
    <>
      <TwAutocomplete
        label={hideLabel ? undefined : label}
        name="driver"
        filterOptions={(x: any): any => x}
        renderOption={(props, option): React.ReactNode => {
          return <RenderOption key={option.id} props={props} option={option} />;
        }}
        optionList={driverFilterLabels}
        value={selectedDriver || null}
        onChange={(_, newValue: TwAutocompleteOption | null): void => {
          const newDriver = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
          setSelectedDriver(newDriver);
          onChange(newValue?.id || '');
        }}
        onInputChange={(event: SyntheticEvent<Element, Event>, value: string | null): void => {
          if (value === '' && !driversIdsToFilter) {
            setDriverFilterLabels([]);
            setDisableNoOption(true);
          } else {
            if (value) {
              fetchDriversForFilter(value);
              setDisableNoOption(false);
            }
          }
        }}
        listAction={
          (userData?.role !== USER_ROLE.DRIVER && !readOnly) || !noActions
            ? {
                label: 'New driver',
                action: handleOpenCreateDriver
              }
            : undefined
        }
        error={hasError}
        readonly={readOnly}
        showEndAdornment={!readOnly}
        disabled={disabled}
        sx={sx}
        disableNoOption={disableNoOption}
      />

      {openCreateDriverModal && (
        <CreateDriver
          open={openCreateDriverModal}
          onClose={handleCloseCreateDriver}
          onSuccess={handleCreateDriverSuccess}
          fromDocManager={fromDocManager}
        />
      )}
    </>
  );
};

export default TwDriverSelector;
